import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useEditor } from '../../context/EditorContext';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { Label } from '../ui/label';
import { WpDialog, NumberInput, Field } from '../dialogs/DialogShell';
import { findAllMatches, selectPlainRange, highlightPlainRange, clearFindHighlight, supportsHighlightApi, replaceAllInPages } from '../../lib/findReplace';
import { editingStore, saveSelection, isEditingText } from '../../lib/editingStore';
import { X, ChevronUp, ChevronDown, Search, Replace as ReplaceIcon } from 'lucide-react';
import { toast } from 'sonner';

const sameMatch = (a, b) => !!a && !!b && a.objectId === b.objectId && a.start === b.start && a.end === b.end && String(a.cell) === String(b.cell);

/**
 * Publisher-style Find and Replace task pane (non-modal, floats over the workspace).
 * Opened through state.findPane = { mode: 'find' | 'replace', query? }.
 */
export const FindReplacePane = () => {
  const { state, dispatch, rawDispatch } = useEditor();
  const pane = state.findPane;
  const [mode, setMode] = useState(pane?.mode || 'find');
  const [query, setQuery] = useState(pane?.query || '');
  const [replaceWith, setReplaceWith] = useState('');
  const [matchCase, setMatchCase] = useState(false);
  const [wholeWord, setWholeWord] = useState(false);
  const [current, setCurrent] = useState(null); // the match currently highlighted
  const [status, setStatus] = useState('');
  const inputRef = useRef(null);
  const paneRef = useRef(null);

  // The match highlight lives in the document; drop it whenever there is no current match, the edited box changes,
  // or the pane closes.
  useEffect(() => {
    if (!current || !pane || (state.editingObjectId && state.editingObjectId !== current.objectId)) clearFindHighlight();
  }, [current, pane, state.editingObjectId]);
  useEffect(() => () => clearFindHighlight(), []);

  // Re-opening (or switching mode from the ribbon) refreshes the pane state and focuses the search box.
  useEffect(() => {
    if (!pane) return;
    setMode(pane.mode || 'find');
    if (pane.query) setQuery(pane.query);
    setCurrent(null);
    setStatus('');
    // Focus the search box once it is mounted (retry briefly: the pane may still be rendering / a menu may be closing).
    let tries = 0;
    const focus = () => {
      const el = inputRef.current;
      if (el && document.body.contains(el)) { el.focus({ preventScroll: true }); el.select(); if (document.activeElement === el) return; }
      if (tries++ < 10) setTimeout(focus, 40);
    };
    const t = setTimeout(focus, 20);
    return () => clearTimeout(t);
    // deliberately keyed on the open counter + mode only: pane.query is read when the pane is (re)opened, not whenever the
    // ribbon's pane object is recreated — otherwise typing in the ribbon's search box would reset this pane's state
  }, [pane?.nonce, pane?.mode]); // eslint-disable-line react-hooks/exhaustive-deps

  const matches = useMemo(() => findAllMatches(state.pages, query.trim() ? query : '', { matchCase, wholeWord }), [state.pages, query, matchCase, wholeWord]);
  const currentIndex = useMemo(() => matches.findIndex((m) => sameMatch(m, current)), [matches, current]);

  // Generation counter: a pending "highlight once the box is editable" loop must stop when the pane closes or a
  // newer navigation supersedes it (otherwise a late tick could paint a highlight after the pane was dismissed).
  const gen = useRef(0);
  const close = useCallback(() => {
    gen.current += 1;
    clearFindHighlight();
    rawDispatch({ type: 'SET_FIND_PANE', payload: null });
  }, [rawDispatch]);

  // A document change that did not come from this pane (typing, Undo, Redo…) invalidates the transient status
  // line; the live match count underneath always reflects the current document.
  const lastActionAt = useRef(0);
  const markAction = () => { lastActionAt.current = Date.now(); };
  const firstPages = useRef(true);
  useEffect(() => {
    if (firstPages.current) { firstPages.current = false; return; }
    if (Date.now() - lastActionAt.current < 600) return;
    setStatus('');
    setCurrent(null);
  }, [state.pages]);

  /** Navigate to a match: page → select object → (text boxes) edit mode with the match selected. */
  const goTo = useCallback((m) => {
    if (!m) return;
    markAction();
    if (state.viewMode === 'master') dispatch({ type: 'SET_VIEW_MODE', payload: 'normal' });
    if (m.pageIndex !== state.currentPageIndex) dispatch({ type: 'SET_CURRENT_PAGE', payload: m.pageIndex });
    dispatch({ type: 'SELECT_OBJECT', payload: { id: m.objectId } });
    setCurrent(m);
    if (m.cell) {
      rawDispatch({ type: 'SET_EDITING', payload: null });
      setStatus(`Match in table cell (row ${m.cell[0] + 1}, column ${m.cell[1] + 1})`);
      setTimeout(() => document.querySelector(`[data-testid="canvas-object-${m.objectId}"]`)?.scrollIntoView({ block: 'center', inline: 'center' }), 20);
      return;
    }
    rawDispatch({ type: 'SET_EDITING', payload: m.objectId });
    // Remember which pane control had focus so it gets it back once the match is highlighted.
    const returnTo = paneRef.current?.contains(document.activeElement) ? document.activeElement : inputRef.current;
    const myGen = ++gen.current;
    let tries = 0;
    const tick = () => {
      if (gen.current !== myGen) return; // superseded or pane closed
      const el = editingStore.el;
      if (el && editingStore.objectId === m.objectId) {
        el.scrollIntoView({ block: 'center', inline: 'center' });
        const range = highlightPlainRange(el, m.start, m.end);
        if (range && supportsHighlightApi()) {
          // Painted with the Custom Highlight API; remember the range as the box's text selection (ribbon
          // formatting commands restore it) and hand focus back to the pane so Enter keeps stepping.
          editingStore.savedRange = range.cloneRange();
          (returnTo && document.body.contains(returnTo) ? returnTo : inputRef.current)?.focus({ preventScroll: true });
        } else {
          // Browsers without the API fall back to the native selection (focus stays in the text box).
          selectPlainRange(el, m.start, m.end);
          saveSelection();
        }
        return;
      }
      if (tries++ < 20) setTimeout(tick, 30);
    };
    setTimeout(tick, 30);
  }, [dispatch, rawDispatch, state.currentPageIndex, state.viewMode]);

  const step = useCallback((dir) => {
    if (!query) { setStatus('Type the text you want to find.'); inputRef.current?.focus(); return; }
    if (!matches.length) { setStatus(`No matches for “${query}”.`); return; }
    let idx;
    if (currentIndex === -1) {
      // start from the current page / after the selected object
      const selId = state.selectedObjectIds[0];
      const fromSel = selId ? matches.findIndex((m) => m.objectId === selId) : -1;
      const fromPage = matches.findIndex((m) => m.pageIndex >= state.currentPageIndex);
      idx = fromSel >= 0 ? fromSel : fromPage >= 0 ? fromPage : 0;
      if (dir < 0 && fromSel < 0) idx = matches.length - 1;
    } else {
      idx = currentIndex + dir;
      if (idx >= matches.length) { idx = 0; toast.info('Reached the end of the publication, continuing from the beginning.', { id: 'find-wrap' }); }
      if (idx < 0) { idx = matches.length - 1; toast.info('Reached the beginning of the publication, continuing from the end.', { id: 'find-wrap' }); }
    }
    goTo(matches[idx]);
    setStatus(`${idx + 1} of ${matches.length} match${matches.length === 1 ? '' : 'es'}`);
  }, [query, matches, currentIndex, goTo, state.selectedObjectIds, state.currentPageIndex]);

  /** Replace the current match. Returns true when a replacement was made (false = only navigated). */
  const replaceOne = useCallback(() => {
    if (!query) { setStatus('Type the text you want to find.'); inputRef.current?.focus(); return false; }
    if (!matches.length) { setStatus(`No matches for “${query}”.`); return false; }
    const m = currentIndex >= 0 ? matches[currentIndex] : null;
    if (!m) { step(1); return false; } // first click highlights the first match, like Publisher
    markAction();
    if (m.cell) {
      const obj = state.pages[m.pageIndex]?.objects.find((o) => o.id === m.objectId);
      if (!obj) return false;
      const cells = obj.properties.cells.map((row) => row.map((c) => ({ ...c })));
      const cell = cells[m.cell[0]][m.cell[1]];
      cell.text = cell.text.slice(0, m.start) + replaceWith + cell.text.slice(m.end);
      dispatch({ type: 'UPDATE_OBJECT_PROPS_ANYWHERE', payload: [{ pageIndex: m.pageIndex, id: m.objectId, props: { cells } }] });
    } else {
      const el = editingStore.el;
      const ready = isEditingText() && editingStore.objectId === m.objectId;
      if (!ready) { goTo(m); setStatus('Match selected — click Replace again.'); return false; }
      const returnTo = paneRef.current?.contains(document.activeElement) ? document.activeElement : inputRef.current;
      el.focus({ preventScroll: true });
      if (!selectPlainRange(el, m.start, m.end)) { setStatus('Could not locate the match in the text box. Click Find Next and try again.'); return false; }
      document.execCommand('insertText', false, replaceWith);
      saveSelection();
      editingStore.sync && editingStore.sync();
      clearFindHighlight();
      returnTo?.focus({ preventScroll: true });
    }
    setCurrent(null);
    const remaining = matches.length - 1;
    setStatus(remaining ? `Replaced. ${remaining} match${remaining === 1 ? '' : 'es'} left.` : 'Replaced the last match.');
    return true;
  }, [matches, currentIndex, query, replaceWith, goTo, step, dispatch, state.pages]);

  // After a single replacement the next match (from the same position) becomes current automatically.
  const pendingNext = useRef(false);
  useEffect(() => {
    if (!pendingNext.current) return;
    pendingNext.current = false;
    if (matches.length) step(1);
    // keyed on `matches` only: the advance must wait for the document update that follows the replacement. `step`
    // changes identity in the same commit as setCurrent(null) — listing it would advance at once, on the stale matches
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matches]);
  const replaceAndNext = () => {
    const replaced = replaceOne();
    // Only chain to the next match when text actually changed; a plain navigation must not auto-advance.
    pendingNext.current = replaced;
  };

  const replaceAll = useCallback(() => {
    if (!query) { setStatus('Type the text you want to find.'); return; }
    if (isEditingText()) rawDispatch({ type: 'SET_EDITING', payload: null });
    const { updates, total } = replaceAllInPages(state.pages, query, replaceWith, { matchCase, wholeWord });
    if (!total) { setStatus(`No matches for “${query}”.`); return; }
    markAction();
    dispatch({ type: 'UPDATE_OBJECT_PROPS_ANYWHERE', payload: updates });
    setCurrent(null);
    setStatus(`Replaced ${total} occurrence${total === 1 ? '' : 's'} in ${updates.length} object${updates.length === 1 ? '' : 's'}.`);
    toast.success(`Replaced ${total} occurrence${total === 1 ? '' : 's'}.`);
  }, [query, replaceWith, matchCase, wholeWord, state.pages, dispatch, rawDispatch]);

  if (!pane) return null;
  const onKey = (e) => {
    if (e.key === 'Escape') { e.preventDefault(); close(); }
    if (e.key === 'Enter') { e.preventDefault(); step(e.shiftKey ? -1 : 1); }
  };

  return (
    <aside
      ref={paneRef}
      className="absolute top-3 right-3 z-[60] w-[320px] max-w-[calc(100vw-24px)] rounded-lg border border-border bg-card/95 text-card-foreground shadow-xl backdrop-blur-md"
      data-testid="find-replace-pane"
      data-keep-editing
      data-ribbon
      role="dialog"
      aria-label="Find and Replace"
    >
      <header className="flex items-center gap-2 px-3 pt-2.5 pb-2 border-b border-border">
        <Search className="h-4 w-4 text-primary" />
        <h2 className="font-outfit text-sm font-semibold flex-1">Find and Replace</h2>
        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={close} aria-label="Close Find and Replace" data-testid="find-pane-close"><X className="h-4 w-4" /></Button>
      </header>
      <div className="p-3 space-y-3 text-sm">
        <div className="grid grid-cols-2 gap-1 rounded-md bg-muted p-1" role="radiogroup" aria-label="Mode">
          {[['find', 'Find'], ['replace', 'Replace']].map(([m, l]) => (
            <button key={m} type="button" role="radio" aria-checked={mode === m} onClick={() => setMode(m)} className={`h-7 rounded-sm text-[13px] transition-colors ${mode === m ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`} data-testid={`find-pane-mode-${m}`}>{l}</button>
          ))}
        </div>
        <div className="space-y-1">
          <Label htmlFor="find-query" className="text-xs text-muted-foreground">Search for</Label>
          <Input id="find-query" ref={inputRef} value={query} onChange={(e) => { setQuery(e.target.value); setCurrent(null); setStatus(''); }} onKeyDown={onKey} placeholder="Text to find" className="h-8" data-testid="find-pane-query" />
        </div>
        {mode === 'replace' && (
          <div className="space-y-1">
            <Label htmlFor="find-replace" className="text-xs text-muted-foreground">Replace with</Label>
            <Input id="find-replace" value={replaceWith} onChange={(e) => setReplaceWith(e.target.value)} onKeyDown={(e) => { if (e.key === 'Escape') close(); }} placeholder="Replacement text" className="h-8" data-testid="find-pane-replace-with" />
          </div>
        )}
        <div className="flex flex-wrap gap-x-4 gap-y-1.5">
          <label className="flex items-center gap-2 text-[13px] cursor-pointer"><Checkbox checked={wholeWord} onCheckedChange={(v) => { setWholeWord(!!v); setCurrent(null); setStatus(''); }} data-testid="find-pane-whole-word" /> Match whole word only</label>
          <label className="flex items-center gap-2 text-[13px] cursor-pointer"><Checkbox checked={matchCase} onCheckedChange={(v) => { setMatchCase(!!v); setCurrent(null); setStatus(''); }} data-testid="find-pane-match-case" /> Match case</label>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" className="h-8" onClick={() => step(1)} data-testid="find-pane-next"><ChevronDown className="h-4 w-4 mr-1" /> Find Next</Button>
          <Button size="sm" variant="outline" className="h-8" onClick={() => step(-1)} data-testid="find-pane-prev"><ChevronUp className="h-4 w-4 mr-1" /> Previous</Button>
        </div>
        {mode === 'replace' && (
          <div className="flex items-center gap-2">
            <Button size="sm" variant="secondary" className="h-8" onClick={replaceAndNext} disabled={!query} data-testid="find-pane-replace"><ReplaceIcon className="h-4 w-4 mr-1" /> Replace</Button>
            <Button size="sm" variant="secondary" className="h-8" onClick={replaceAll} disabled={!query} data-testid="find-pane-replace-all">Replace All</Button>
          </div>
        )}
        <div className="text-xs text-muted-foreground min-h-[16px] space-y-0.5" aria-live="polite">
          {/* Live count always reflects the current document (so Undo / edits are visible immediately). */}
          <p data-testid="find-pane-count">{query ? `${matches.length} match${matches.length === 1 ? '' : 'es'} in the publication` : 'Searches every text box and table on every page.'}</p>
          {status && <p className="text-foreground" data-testid="find-pane-status">{status}</p>}
        </div>
      </div>
    </aside>
  );
};

/** Find ▸ Go To Page… */
export const GoToPageDialog = () => {
  const { state, dispatch, closeDialog } = useEditor();
  const [n, setN] = useState(state.currentPageIndex + 1);
  const go = () => { dispatch({ type: 'SET_CURRENT_PAGE', payload: Math.max(0, Math.min(state.pages.length - 1, Math.round(n) - 1)) }); closeDialog(); };
  return (
    <WpDialog name="go-to-page" title="Go To Page" description={`Enter a page number between 1 and ${state.pages.length}.`} width="w-[340px]" onOk={go} okLabel="Go" onCancel={closeDialog}>
      <Field label="Page number">
        <NumberInput value={n} onChange={setN} min={1} max={state.pages.length} step={1} testId="go-to-page-input" />
      </Field>
    </WpDialog>
  );
};
